import { useContext } from "react";
import { useSelector } from "react-redux";
import { Boundaries } from "@planzone/core";
import {
  CapacityGranularity,
  CapacitySort,
  Filter,
  Id,
  ProjectWorkload,
} from "@planzone/dto";
import { CoreContext } from "../context";

interface ViewModel {
  isRequestPending: boolean;
  isRequestSuccess: boolean;
  isRequestFailure: boolean;
  content: ProjectWorkload[];
  currentPage: number;
  canLoadMore: boolean;
  totalElements: number;
  retrieveWorkloadProjects(
    request: Boundaries["retrieveWorkloadProjects"]["request"],
  ): void;
}

function useRetrieveWorkloadProjects(
  resourceId: Id,
  from: string,
  to: string,
  granularity: CapacityGranularity,
  filter: Filter | undefined,
  sort: CapacitySort | undefined,
  size: number,
): ViewModel {
  const { selector, core } = useContext(CoreContext);

  return {
    isRequestPending: useSelector(() =>
      selector.isRetrieveWorkloadProjectsRequestPending(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestSuccess: useSelector(() =>
      selector.isRetrieveWorkloadProjectsRequestSuccess(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestFailure: useSelector(() =>
      selector.isRetrieveWorkloadProjectsRequestFailure(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    content: useSelector(() =>
      selector.getWorkloadProjectsContent(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    currentPage: useSelector(() =>
      selector.getWorkloadProjectsCurrentPage(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    canLoadMore: useSelector(() =>
      selector.canLoadMoreWorkloadProjects(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    totalElements: useSelector(() =>
      selector.getWorkloadProjectsTotalElements(
        resourceId,
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    retrieveWorkloadProjects: core.retrieveWorkloadProjects,
  };
}

export { useRetrieveWorkloadProjects };
